import { useQuery } from '@tanstack/react-query'
import { Banner, Button, ErrorState, NavigationLink, Skeleton } from '@shared/design-system/ui'
import { fetchKycVerification, verificationKeys } from '../api/verification'
import { getOnboardingAction, onboardingActions } from '../lib/onboarding-action'
import { describeBankReadiness } from '../lib/bank-account-feedback'
import { getRequirementSummaryLabel } from '../lib/requirement-label'
import { onboardingOperationStatus } from '../model/onboarding-status'
import { noticeAction } from './receipts-account-notice.css'

/** Read-only account guidance, composed by the page alongside the wallet feature. */
export function ReceiptsAccountNotice() {
  const verification = useQuery({
    queryKey: verificationKeys.view,
    queryFn: fetchKycVerification,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  })
  const view = verification.data
  if (!view && verification.isPending) return <Skeleton height={80} />
  if (!view) {
    return (
      <ErrorState
        title="Não foi possível consultar a situação do cadastro"
        description="Tente carregar novamente. O saldo e os repasses continuam em consultas separadas."
        onRetry={() => {
          void verification.refetch()
        }}
      />
    )
  }
  const stale =
    verification.isError || view.eligibility.syncFailure || view.verification?.syncFailure
  const operation = onboardingOperationStatus(view.onboarding.operations.payouts, 'Recebimentos')
  const bank = view.bank?.readiness
  const nextStep = view.onboarding.nextStep
  const nextAction = getOnboardingAction(nextStep)
  const supportRequired = nextAction.kind === 'support'
  const identityRequired = nextAction.kind === 'identity'
  const profileRequired = nextAction.kind === 'profile'
  const awaitingReview = nextAction.kind === 'review'
  // Disabled payouts do not prove a bank problem. Follow Identity's next step first.
  const bankNeedsCorrection =
    !supportRequired &&
    !identityRequired &&
    !profileRequired &&
    !awaitingReview &&
    bank &&
    !bank.ready &&
    bank.reason &&
    [
      'no_default_account',
      'default_account_errored',
      'default_account_verification_failed',
      'external_account_required',
    ].includes(bank.reason)
  const requirement =
    nextStep.code === 'fix_field'
      ? getRequirementSummaryLabel(nextStep.requirementId, 'form')
      : undefined
  const title = !view.linked
    ? 'Conta Connect não vinculada'
    : bankNeedsCorrection
      ? 'Confira a conta bancária de recebimento'
      : `Recebimentos no cadastro: ${operation.status}`
  const description = !view.linked
    ? 'Conclua o cadastro para vincular sua conta de recebimento.'
    : supportRequired
      ? 'Fale com o suporte da VendePay para continuar a análise. Consulte a orientação no cadastro Connect.'
      : identityRequired
        ? 'Conclua a verificação de identidade no cadastro Connect para continuar.'
        : profileRequired
          ? requirement
            ? `Confira ${requirement} para continuar seu cadastro.`
            : 'Complete ou corrija os dados cadastrais para continuar.'
          : awaitingReview
            ? 'O cadastro está em análise. Acompanhe a verificação para consultar o resultado.'
            : bankNeedsCorrection
              ? describeBankReadiness(bank)
              : view.onboarding.operations.payouts === 'in_review'
                ? 'O cadastro está em análise. Acompanhe a verificação para consultar o resultado.'
                : view.onboarding.operations.payouts === 'blocked'
                  ? view.onboarding.blockedBy.payouts === 'vendepay'
                    ? 'Há uma restrição cadastral na VendePay. Ela não representa controle do envio bancário pela Stripe.'
                    : 'Há um impedimento informado no cadastro. Consulte a verificação para corrigir as pendências.'
                  : view.onboarding.operations.payouts === 'waiting'
                    ? requirement
                      ? `Confira ${requirement} para continuar seu cadastro.`
                      : 'Conclua as pendências do cadastro para habilitar os recebimentos.'
                    : 'Recebimentos habilitados na conta cadastrada. O estado de cada repasse é mostrado abaixo.'
  const actionKind = !view.linked
    ? 'account'
    : bankNeedsCorrection
      ? 'bank'
      : nextAction.kind === 'bank'
        ? 'account'
        : nextAction.kind
  const action = onboardingActions[actionKind]
  const actionLabel =
    actionKind === 'account'
      ? 'Conferir cadastro Connect'
      : actionKind === 'bank'
        ? 'Conferir cadastro bancário'
        : actionKind === 'review'
          ? 'Acompanhar análise do cadastro'
          : action.action
  return (
    <>
      {stale ? (
        <Banner
          data-testid="receipts-account-stale"
          tone="warning"
          title="Consulta do cadastro desatualizada"
        >
          A última situação conhecida continua visível. Atualize a consulta ou confira o cadastro.
          <span className={noticeAction}>
            <Button
              data-testid="receipts-account-retry-btn"
              variant="secondary"
              onClick={() => {
                void verification.refetch()
              }}
            >
              Tentar novamente
            </Button>
          </span>
        </Banner>
      ) : null}
      <Banner
        data-testid="receipts-account-notice"
        tone={
          view.linked && bank?.ready && view.onboarding.operations.payouts === 'released'
            ? 'info'
            : 'warning'
        }
        title={title}
      >
        {description}
        {view.linked && bankNeedsCorrection ? (
          <span> Recebimentos no cadastro: {operation.status}.</span>
        ) : null}
        {view.linked && !bankNeedsCorrection && bank?.reason !== 'payouts_disabled' ? (
          <span> {bank ? describeBankReadiness(bank) : 'Situação bancária indisponível.'}</span>
        ) : null}
        <span className={noticeAction}>
          <NavigationLink data-testid="receipts-account-cadastro-link" href={action.href}>
            {actionLabel}
          </NavigationLink>
        </span>
      </Banner>
    </>
  )
}
